import {
  bountyTargetError,
  draftGoingLive,
  memberCount,
  payoutLabel,
  pointsGoingLive,
  pointsOnStep,
  togglePaysLine,
} from '@arthelper/shared/bounties/confirm';
import { DEFAULT_STEP_BOUNTY_POINTS } from '@arthelper/shared/bounties/score';
import { TARGET_AT_LEAST_ONE } from '@arthelper/shared/recurrence/step-rules';

/** The rule an admin reads. The server says the same sentence (`validateBounty`). */
export const BOUNTY_NEEDS_A_NAME = 'A bounty needs a name.';

export {
  bountyTargetError,
  DEFAULT_STEP_BOUNTY_POINTS,
  draftGoingLive,
  memberCount,
  payoutLabel,
  pointsGoingLive,
  pointsOnStep,
  TARGET_AT_LEAST_ONE,
  togglePaysLine,
};

/** Blank — including spaces — is not a name. Checked before the create request. */
export function bountyNameError(name: string): string | null {
  return name.trim() ? null : BOUNTY_NEEDS_A_NAME;
}

function people(count: number): string {
  return memberCount(count);
}

/** Publish confirm on the editor. The count is the audience, the points are `pointsReward`. */
export function publishMatchLine(points: number, audienceCount: number, extra = ''): string {
  const verb = audienceCount === 1 ? 'matches' : 'match';
  return `It pays ${payoutLabel(points)} to each eligible member who finishes it from now on — ${people(audienceCount)} ${verb} its audience.${extra}`;
}

/** Publish confirm on the bounties list. Same payout, same audience noun. */
export function publishSelectsLine(points: number, audienceCount: number, extra = ''): string {
  return `It pays ${payoutLabel(points)} to each eligible member who finishes it from now on — its audience selects ${people(audienceCount)}.${extra}`;
}

type ErrorMessage = { message?: unknown };

function messagesOf(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const message = (item as ErrorMessage | null)?.message;
    return typeof message === 'string' && message ? [message] : [];
  });
}

const STATUS_WRAPPER = /^Response not successful: Received status code \d+/;

/**
 * The toast for a failed save. A blank name is the name rule. Any other server sentence
 * wins over Apollo's HTTP status wrapper, including one nested under that 400.
 */
export function bountySaveErrorMessage(error: unknown, name: string): string {
  const blank = bountyNameError(name);
  if (blank) return blank;

  const record = (error ?? {}) as {
    message?: unknown;
    graphQLErrors?: unknown;
    networkError?: { result?: { errors?: unknown } };
    result?: { errors?: unknown };
  };
  const server = [
    ...messagesOf(record.graphQLErrors),
    ...messagesOf(record.networkError?.result?.errors),
    ...messagesOf(record.result?.errors),
    ...(typeof record.message === 'string' ? [record.message] : []),
  ].find((message) => !STATUS_WRAPPER.test(message));
  if (server) return server;

  return error instanceof Error ? error.message : 'Failed to save';
}
